import { useEffect } from "react"
import { I18n } from "react-polyglot-hooks"
import { useRouter } from "next/router"
import CarContextProvider from "scenes/CarScene/CarContext"
import LoadingScene from "scenes/LoadingScene"
import isEmpty from "lodash/isEmpty"
import * as PropTypes from "prop-types"
import { sendCarView } from "scripts/FacebookPixel"
import Car from "models/car"
import { getCarData, getCarsList, getModelData } from "services/webService"
import dynamic from "next/dynamic"
import { captureException } from "@sentry/nextjs"
import { getCarImages } from "helpers/cloudinary"
import getUnavailableCarDestination from "helpers/unavailableCarRedirect"
import car_data from "../../../../../i18n/es/car_data"
import car_i18n from "../../../../../i18n/es/car"
import common from "../../../../../i18n/es/common"
import { getFooterLinks } from "../../../../../src/services/seoService"

const CarScene = dynamic(() => import("scenes/CarScene"))

function Version({ car, cars, slug, extraImages, footerLinks }) {
  const { isFallback } = useRouter()

  useEffect(() => {
    if (car.id) {
      sendCarView([car.id], car.minimumPrice)
    }
  }, [car])

  // if (notFound) return <NotFound />

  if (isFallback || isEmpty(car))
    return (
      <I18n locale="es" phrases={common}>
        <LoadingScene />
      </I18n>
    )

  return (
    <I18n locale="es" phrases={{ ...common, ...car_i18n, ...car_data }}>
      <CarContextProvider car={new Car(car)} cars={cars} slug={slug} extraImages={extraImages} footerLinks={footerLinks}>
        <CarScene />
      </CarContextProvider>
    </I18n>
  )
}

Version.defaultProps = {
  car: {},
  slug: {},
  cars: [],
  footerLinks: [],
  extraImages: []
}

Version.propTypes = {
  car: PropTypes.object,
  slug: PropTypes.object,
  cars: PropTypes.array,
  footerLinks: PropTypes.array,
  extraImages: PropTypes.arrayOf(PropTypes.string)
}

export async function getStaticPaths() {
  const res = await getCarsList("es")
  let data;
  try {
    data = await res.json();
  } catch (error) {
    data = [];
  }

  const paths = process.env.SKIP_PRELOAD ? [] : data.map(item => ({ params: item }));
  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  try {
    const { brand, model, version } = params
    // TODO: remove when solved
    if (version.indexOf(".") !== -1) captureException("Route Version param error", { extra: params })
    const res = await getCarData(`renting-${brand}`, model, version, "es")
    const carData = res.status === 404 ? null : await res.json()

    if (res.status === 404 || carData?.car?.sold) {
      const modelRes = await getModelData(brand, model, "es")

      return {
        redirect: {
          destination: getUnavailableCarDestination(brand, model, modelRes.status),
          statusCode: 301
        },
        revalidate: 60
      }
    }
    const { car, similarCars: cars } = carData

    // Footer Links
    const footerLinksRes = await getFooterLinks()
    const footerLinks = await footerLinksRes.json()

    const extraImages = await getCarImages(car?.id)

    return {
      props: {
        key: car?.id,
        car,
        cars,
        slug: { brand, model, version },
        extraImages,
        footerLinks
      },
      revalidate: 30
    }
  } catch (error) {
    console.error('Error en getStaticProps de version:', error);
    return {
      props: { notFound: true },
      revalidate: 60
    };
  }
}

export default Version
